(() => {
  const SELECTOR = '[data-meltiva-header]';
  const DESKTOP_QUERY = window.matchMedia('(min-width: 990px)');

  const getFocusable = (container) =>
    Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hidden && element.offsetParent !== null);

  const updateCartCount = (root, count) => {
    root.querySelectorAll('[data-mvh-cart-count]').forEach((element) => {
      element.textContent = String(count);
      element.setAttribute('aria-label', `${count} items in cart`);
    });
  };

  const fetchCart = async (root) => {
    try {
      const base = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${base}cart.js`, {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      });

      if (!response.ok) return;

      const cart = await response.json();
      updateCartCount(root, cart.item_count || 0);
    } catch (error) {
      // Preserve the server-rendered count if the request fails.
    }
  };

  const tryOpenCartDrawer = (root, trigger) => {
    const customDrawer = document.querySelector('cart-drawer');

    if (customDrawer && typeof customDrawer.open === 'function') {
      customDrawer.open(trigger);
      return true;
    }

    const detailsDrawer = document.querySelector(
      'details[id*="CartDrawer"], details[data-cart-drawer]'
    );

    if (detailsDrawer) {
      detailsDrawer.open = true;
      return true;
    }

    const existingTrigger = Array.from(
      document.querySelectorAll(
        '[data-cart-drawer-open], [aria-controls="CartDrawer"], .header__icon--cart, .cart-drawer__trigger'
      )
    ).find((element) => !root.contains(element));

    if (existingTrigger) {
      existingTrigger.click();
      return true;
    }

    const genericDrawer = document.querySelector(
      '#CartDrawer, [data-cart-drawer], .cart-drawer'
    );

    if (genericDrawer) {
      genericDrawer.removeAttribute('hidden');
      genericDrawer.setAttribute('aria-hidden', 'false');
      genericDrawer.classList.add('is-open', 'active', 'open');
      document.body.classList.add('overflow-hidden');
      return true;
    }

    return false;
  };

  const initHeader = (root) => {
    if (!root || root.dataset.mvhReady === 'true') return;
    root.dataset.mvhReady = 'true';

    const shop = root.querySelector('[data-mvh-shop]');
    const shopTrigger = root.querySelector('[data-mvh-shop-trigger]');
    const mega = root.querySelector('[data-mvh-mega]');
    const mobileMenu = root.querySelector('[data-mvh-mobile]');
    const mobileOpen = root.querySelector('[data-mvh-mobile-open]');
    const mobileClose = root.querySelector('[data-mvh-mobile-close]');
    const cartTriggers = Array.from(
      root.querySelectorAll('[data-mvh-cart]')
    );

    let shopCloseTimer = null;
    let lastMobileFocus = null;

    const setMegaInert = (value) => {
      if (!mega) return;

      if ('inert' in mega) {
        mega.inert = value;
      }
    };

    const openMega = () => {
      if (!DESKTOP_QUERY.matches || !shop || !shopTrigger || !mega) return;

      window.clearTimeout(shopCloseTimer);
      shop.classList.add('is-open');
      shopTrigger.setAttribute('aria-expanded', 'true');
      mega.setAttribute('aria-hidden', 'false');
      setMegaInert(false);
    };

    const closeMega = (returnFocus = false) => {
      if (!shop || !shopTrigger || !mega) return;

      window.clearTimeout(shopCloseTimer);
      shop.classList.remove('is-open');
      shopTrigger.setAttribute('aria-expanded', 'false');
      mega.setAttribute('aria-hidden', 'true');
      setMegaInert(true);

      if (returnFocus) {
        shopTrigger.focus();
      }
    };

    const scheduleMegaClose = () => {
      window.clearTimeout(shopCloseTimer);
      shopCloseTimer = window.setTimeout(() => closeMega(false), 110);
    };

    const openMobile = () => {
      if (!mobileMenu || !mobileOpen) return;

      closeMega(false);
      lastMobileFocus = document.activeElement;
      mobileMenu.classList.add('is-open');
      mobileMenu.setAttribute('aria-hidden', 'false');
      mobileOpen.setAttribute('aria-expanded', 'true');
      document.body.classList.add('mvh-menu-open');

      if ('inert' in mobileMenu) {
        mobileMenu.inert = false;
      }

      window.requestAnimationFrame(() => {
        const focusable = getFocusable(mobileMenu);
        focusable[0]?.focus();
      });
    };

    const closeMobile = (returnFocus = true) => {
      if (!mobileMenu || !mobileOpen) return;

      mobileMenu.classList.remove('is-open');
      mobileMenu.setAttribute('aria-hidden', 'true');
      mobileOpen.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('mvh-menu-open');

      if ('inert' in mobileMenu) {
        mobileMenu.inert = true;
      }

      if (returnFocus) {
        (lastMobileFocus || mobileOpen).focus();
      }
    };

    setMegaInert(true);

    if (mobileMenu && 'inert' in mobileMenu) {
      mobileMenu.inert = true;
    }

    shopTrigger?.addEventListener('click', (event) => {
      if (!DESKTOP_QUERY.matches) return;

      event.preventDefault();

      if (shop?.classList.contains('is-open')) {
        closeMega(false);
      } else {
        openMega();
      }
    });

    shopTrigger?.addEventListener('keydown', (event) => {
      if (!DESKTOP_QUERY.matches) return;

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();

        if (shop?.classList.contains('is-open')) {
          closeMega(false);
        } else {
          openMega();
        }
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        openMega();
        getFocusable(mega)[0]?.focus();
      }

      if (event.key === 'Escape') {
        closeMega(true);
      }
    });

    shop?.addEventListener('pointerenter', openMega);
    shop?.addEventListener('pointerleave', scheduleMegaClose);
    shop?.addEventListener('focusin', openMega);

    shop?.addEventListener('focusout', (event) => {
      if (!shop.contains(event.relatedTarget)) {
        scheduleMegaClose();
      }
    });

    mega?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMega(true);
      }
    });

    mobileOpen?.addEventListener('click', openMobile);
    mobileClose?.addEventListener('click', () => closeMobile(true));

    mobileMenu?.addEventListener('click', (event) => {
      if (event.target.closest('a[href]')) {
        closeMobile(false);
      }
    });

    mobileMenu?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMobile(true);
        return;
      }

      if (
        event.key !== 'Tab' ||
        !mobileMenu.classList.contains('is-open')
      ) {
        return;
      }

      const focusable = getFocusable(mobileMenu);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    cartTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        closeMega(false);
        closeMobile(false);

        if (tryOpenCartDrawer(root, trigger)) {
          event.preventDefault();
        }
      });
    });

    document.addEventListener('click', (event) => {
      if (shop && !shop.contains(event.target)) {
        closeMega(false);
      }
    });

    window.addEventListener(
      'scroll',
      () => {
        closeMega(false);
      },
      { passive: true }
    );

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;

      closeMega(false);

      if (mobileMenu?.classList.contains('is-open')) {
        closeMobile(true);
      }
    });

    const onBreakpointChange = () => {
      closeMega(false);

      if (DESKTOP_QUERY.matches) {
        closeMobile(false);
      }
    };

    if (typeof DESKTOP_QUERY.addEventListener === 'function') {
      DESKTOP_QUERY.addEventListener('change', onBreakpointChange);
    } else {
      DESKTOP_QUERY.addListener(onBreakpointChange);
    }

    [
      'cart:refresh',
      'cart:updated',
      'cart:change',
      'product:added',
      'ajaxProduct:added',
      'theme:cart:change'
    ].forEach((eventName) => {
      document.addEventListener(eventName, () => {
        window.setTimeout(() => fetchCart(root), 80);
      });
    });

    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target.closest('form');

        if (!form) return;

        const action = form.getAttribute('action') || '';

        if (action.includes('/cart/add')) {
          window.setTimeout(() => fetchCart(root), 500);
          window.setTimeout(() => fetchCart(root), 1400);
        }
      },
      true
    );

    window.addEventListener('pageshow', () => fetchCart(root));

    fetchCart(root);
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach(initHeader);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();